import React, { useEffect, useRef, useState, memo } from 'react';
import 'leaflet/dist/leaflet.css';

const coordenadasPadrao = [-20.359292, -41.247282];

const extrairCoordenadas = (demanda, latitude, longitude) => {
  if (latitude && longitude) {
    return [parseFloat(latitude), parseFloat(longitude)];
  }

  if (demanda?.google_maps_url) {
    const patterns = [
      /place\/(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    ];
    for (const regex of patterns) {
      const match = demanda.google_maps_url.match(regex);
      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return [lat, lng];
        }
      }
    }
  }

  if (demanda?.coordenada) {
    try {
      const coords = demanda.coordenada.split(',').map(c => parseFloat(c.trim()));
      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        return coords;
      }
    } catch (e) {}
  }

  return coordenadasPadrao;
};

const MapaDemandaFixed = ({
  demanda,
  height = '250px',
  latitude,
  longitude,
  showPosteIcon = false,
  posteNumero,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [localizacaoPrecisa, setLocalizacaoPrecisa] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      try {
        // Importar Leaflet dinamicamente
        const L = (await import('leaflet')).default;

        if (cancelled) return;

        // Limpar mapa anterior
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }

        if (!mapRef.current) return;

        // Remover instância anterior que possa estar no container (evita "already initialized")
        if (mapRef.current._leaflet_id) {
          mapRef.current._leaflet_id = null;
        }
        // Limpar classes do Leaflet que ficam no container
        mapRef.current.className = '';
        mapRef.current.innerHTML = '';

        // Configurar ícones
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        });

        const coordenadas = extrairCoordenadas(demanda, latitude, longitude);
        const isPrecisa = !(coordenadas[0] === coordenadasPadrao[0] && coordenadas[1] === coordenadasPadrao[1]);
        const zoom = isPrecisa ? 16 : 13;

        const map = L.map(mapRef.current, {
          center: coordenadas,
          zoom,
          zoomControl: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          dragging: false,
          touchZoom: false,
          boxZoom: false,
          keyboard: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 18,
        }).addTo(map);

        if (showPosteIcon) {
          const posteIcon = L.divIcon({
            className: 'custom-poste-marker',
            html: `<div style="background:#ff6f00;color:white;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-weight:bold;border:3px solid white;box-shadow:0 3px 6px rgba(0,0,0,0.3);font-size:12px;"><i class="fas fa-lightbulb"></i></div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
          });
          const marker = L.marker(coordenadas, { icon: posteIcon }).addTo(map);
          if (posteNumero) {
            marker.bindPopup(`<div style="text-align:center;min-width:150px;"><h4 style="margin:0 0 8px 0;color:#ff6f00;">Poste #${posteNumero}</h4><div style="font-size:11px;color:#666;">${coordenadas[0].toFixed(6)}, ${coordenadas[1].toFixed(6)}</div></div>`);
          }
        } else {
          L.marker(coordenadas).addTo(map);
        }

        setTimeout(() => map.invalidateSize(), 100);
        setTimeout(() => map.invalidateSize(), 500);
        setTimeout(() => map.invalidateSize(), 1000);

        if (cancelled) {
          map.remove();
          return;
        }

        mapInstanceRef.current = map;
        setLocalizacaoPrecisa(isPrecisa);

      } catch (error) {
        console.error('MapaDemandaFixed - Erro ao inicializar:', error);
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [demanda?.id, latitude, longitude]);

  return (
    <div style={{ height, width: '100%', overflow: 'hidden', position: 'relative' }}>
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default memo(MapaDemandaFixed);
